import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { addDaysToDate } from '@shared/calculations/recurrence.ts';
import { crossesMidnight } from '@shared/scheduling-hours.ts';
import type { AvailabilityView } from '@shared/types/index.ts';
import { scheduleApi } from '../../api/endpoints.ts';
import { Alert, Button, Spinner } from '../ui.tsx';
import { todayInTimezone, zonedDateTime } from './format.ts';
import { formatLocalTime, formatShortDate } from '../../i18n/datetime.ts';

/**
 * The week at a glance, drawn from what would actually block a booking.
 *
 * Seven day columns, one shared time axis, and every busy stretch for the
 * chosen people (or the whole company) painted where it falls: the company's
 * own visits with their customer, unassigned work that holds the hour for
 * everybody, and external commitments as a plain "Busy" — an interval with
 * deliberately nothing else to show. Clicking open space picks that date and
 * time for the form; the ghost outline previews the visit's length so a slot
 * that will not fit is visible before the server says no.
 *
 * The grid thinks entirely in the schedule's own timezone (the location's
 * clock, delivered by the endpoint) — a dispatcher in another zone sees the
 * same calendar the company keeps.
 */

const SNAP_MINUTES = 15;
const HOUR_HEIGHT = 44; // px per hour
const DEFAULT_START_HOUR = 6;
const DEFAULT_END_HOUR = 21;

interface DayBlock {
  key: string;
  startMinutes: number;
  endMinutes: number;
  kind: 'visit' | 'unassigned' | 'external' | 'unavailable' | 'time_off';
  label: string;
  personNames: string[];
}

export function AvailabilityPicker({
  userIds,
  teamId,
  durationMinutes,
  selected,
  onPick,
  excludeAppointmentId,
  excludeSeriesId,
}: {
  /** Whose calendars to consult; empty = the whole company's. */
  userIds: string[];
  teamId?: string | null;
  durationMinutes: number;
  /** The date and time currently chosen in the form, to ghost on the grid. */
  selected?: { date: string; time: string } | null;
  onPick: (date: string, timeLocal: string) => void;
  excludeAppointmentId?: string;
  excludeSeriesId?: string;
}) {
  const { t } = useTranslation();
  // Centered on the pre-filled selection at mount (e.g. a visit's current
  // date). From then on it's driven only by navigation (Today/prev/next) and
  // by the effect below — never recomputed from `selected` on every render,
  // or Today/prev/next would immediately snap back to the original date.
  const [anchor, setAnchor] = useState<string | null>(selected?.date ?? null);
  // The schedule's own clock, learned from the first availability answer so
  // the un-anchored week starts on the location's today, not the browser's.
  const [scheduleZone, setScheduleZone] = useState<string | undefined>(undefined);

  // The viewport holds still while the selection moves inside it — clicking
  // a slot already lands inside the visible week, so no recenter and no
  // refetch, just the new ghost. Only a `selected` date landing outside the
  // *current* window (typed into the date field, a fresh prefill) drags the
  // viewport to it — re-checked fresh against the window each time
  // `selected.date` itself changes, not against a memory of past picks, so a
  // later external jump back to an earlier date still recenters correctly.
  // Deliberately keyed on `selected.date` alone: reacting to `scheduleZone`
  // too would let the one-time timezone update shortly after the first
  // fetch resolves re-trigger this same snap-back once more, if it lands
  // after the user has already navigated away with Today/prev/next.
  useEffect(() => {
    if (!selected) return;
    setAnchor((current) => {
      const base = current ?? todayInTimezone(scheduleZone);
      return selected.date < base || selected.date > addDaysToDate(base, 6) ? selected.date : current;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.date]);

  const start = anchor ?? todayInTimezone(scheduleZone);
  const days = useMemo(() => Array.from({ length: 7 }, (_, index) => addDaysToDate(start, index)), [start]);
  const from = days[0]!;
  const to = days[6]!;

  const availability = useQuery({
    queryKey: ['availability', from, to, teamId, userIds.join(','), excludeAppointmentId ?? '', excludeSeriesId ?? ''],
    queryFn: () =>
      scheduleApi.availability({
        from,
        to,
        teamId,
        userIds,
        ...(excludeAppointmentId ? { excludeAppointmentId } : {}),
        ...(excludeSeriesId ? { excludeSeriesId } : {}),
      }),
  });

  const view = availability.data;
  if (view && view.timezone !== scheduleZone) setScheduleZone(view.timezone);
  const nameOf = useMemo(() => {
    const names = new Map((view?.people ?? []).map((person) => [person.userId, person.fullName]));
    return (userId: string) => names.get(userId) ?? t('availability.someone');
  }, [view, t]);

  // Every block, sliced per visible day in the schedule's own timezone.
  const { blocksByDay, buffer } = useMemo(() => {
    const byDay = new Map<string, DayBlock[]>();
    if (!view) return { blocksByDay: byDay, buffer: 0 };
    const dayIndex = new Set(days);

    const add = (
      startsAt: string,
      endsAt: string,
      kind: DayBlock['kind'],
      label: string,
      personNames: string[],
      key: string,
    ) => {
      const startZ = zonedDateTime(startsAt, view.timezone);
      const endZ = zonedDateTime(endsAt, view.timezone);
      for (const day of dayIndex) {
        if (startZ.date > day || endZ.date < day) continue;
        const startMinutes = startZ.date === day ? minutesOf(startZ.time) : 0;
        const endMinutes = endZ.date === day ? minutesOf(endZ.time) : 24 * 60;
        if (endMinutes <= startMinutes) continue;
        const list = byDay.get(day) ?? [];
        list.push({ key: `${key}:${day}`, startMinutes, endMinutes, kind, label, personNames });
        byDay.set(day, list);
      }
    };

    for (const [index, visit] of view.visits.entries()) {
      // Cross-location blocks arrive without row ids on purpose.
      const key = visit.appointmentId ?? `other-${index}`;
      const crewNames = visit.assignedUserIds.map(nameOf);
      if (visit.assignedUserIds.length === 0) {
        add(visit.startsAt, visit.endsAt, 'unassigned', t('availability.unassignedBlock'), [], key);
      } else {
        add(
          visit.startsAt,
          visit.endsAt,
          'visit',
          visit.customerName ?? t('availability.anotherLocation'),
          crewNames,
          key,
        );
      }
    }
    for (const [index, block] of view.external.entries()) {
      add(
        block.startsAt,
        block.endsAt,
        'external',
        block.status === 'tentative' ? t('availability.tentativeBlock') : t('availability.busyBlock'),
        [nameOf(block.userId)],
        `ext-${index}`,
      );
    }
    for (const [index, block] of (view.unavailable ?? []).entries()) {
      add(
        block.startsAt,
        block.endsAt,
        block.source === 'time_off' ? 'time_off' : 'unavailable',
        t(block.source === 'time_off' ? 'planning.timeOff' : 'planning.unavailable'),
        [nameOf(block.userId)],
        `unavailable-${index}`,
      );
    }
    return { blocksByDay: byDay, buffer: view.bufferMinutes };
  }, [view, days, nameOf, t]);

  // The visible hours open on the scope's working window (9-to-5 weekdays by
  // default) with an hour of margin, then stretch to hold whatever is on the
  // calendar — an early start or a late job cannot hide above or below it.
  //
  // A window that crosses midnight has work at BOTH ends of every column —
  // ten at night and six the next morning are the same shift — so it opens on
  // the whole day rather than on a margin around a range that would cover
  // everything anyway.
  const overnight = view ? crossesMidnight(view.hours) : false;
  const { startHour, endHour } = useMemo(() => {
    let earliest = view ? (overnight ? 0 : minutesOf(view.hours.startLocal) - 60) : DEFAULT_START_HOUR * 60;
    let latest = view ? (overnight ? 24 * 60 : minutesOf(view.hours.endLocal) + 60) : DEFAULT_END_HOUR * 60;
    for (const blocks of blocksByDay.values()) {
      for (const block of blocks) {
        earliest = Math.min(earliest, block.startMinutes - buffer);
        latest = Math.max(latest, block.endMinutes + buffer);
      }
    }
    if (selected) {
      earliest = Math.min(earliest, minutesOf(selected.time));
      latest = Math.max(latest, minutesOf(selected.time) + durationMinutes);
    }
    return {
      startHour: Math.max(0, Math.floor(earliest / 60)),
      endHour: Math.min(24, Math.ceil(latest / 60)),
    };
  }, [view, blocksByDay, buffer, selected, durationMinutes, overnight]);

  // Everything outside working hours is shaded, not blocked: hours steer the
  // eye, and a deliberate out-of-hours booking stays the office's call.
  const workDays = useMemo(() => new Set(view?.hours.days ?? []), [view]);
  const weekdayOfDay = (day: string) => new Date(`${day}T12:00:00Z`).getUTCDay();

  const offHoursBands = (day: string) => {
    if (!view) return [];
    const open = minutesOf(view.hours.startLocal);
    const close = minutesOf(view.hours.endLocal);

    if (!overnight) {
      if (!workDays.has(weekdayOfDay(day))) return [{ top: 0, height: 100 }];
      const bands: Array<{ top: number; height: number }> = [];
      if (open > startHour * 60) bands.push({ top: 0, height: yOf(open) });
      if (close < endHour * 60) bands.push({ top: yOf(close), height: 100 - yOf(close) });
      return bands;
    }

    /*
      A night shift lands on a column in two pieces that belong to two
      different shifts, and each is lit by its own day:

        00:00 → close   the tail of YESTERDAY's shift. Monday morning is only
                        working time if Sunday night was a shift, which is
                        exactly why Saturday mornings are lit for a Monday-to-
                        Friday night crew and Sunday mornings are not.
        open  → 24:00   the head of TODAY's.

      Reading both off today's weekday would light Monday morning for a crew
      whose week starts on Monday night — an hour before they have started.
    */
    const startsToday = workDays.has(weekdayOfDay(day));
    const endedToday = workDays.has(weekdayOfDay(addDaysToDate(day, -1)));
    if (!startsToday && !endedToday) return [{ top: 0, height: 100 }];

    const bands: Array<{ top: number; height: number }> = [];
    const top = endedToday ? yOf(close) : 0;
    const bottom = startsToday ? yOf(open) : 100;
    if (bottom > top) bands.push({ top, height: bottom - top });
    return bands;
  };

  const windowMinutes = (endHour - startHour) * 60;
  const columnHeight = ((endHour - startHour) * HOUR_HEIGHT).toFixed(0);
  const yOf = (minutes: number) => ((minutes - startHour * 60) / windowMinutes) * 100;
  const heightOf = (minutes: number) => (minutes / windowMinutes) * 100;

  // Whether the ghosted selection collides with anything, travel buffer
  // included — the same padding the server applies before it refuses.
  const selectionClash = useMemo(() => {
    if (!selected) return false;
    const blocks = blocksByDay.get(selected.date) ?? [];
    const startMin = minutesOf(selected.time);
    const endMin = startMin + durationMinutes;
    return blocks.some(
      (block) =>
        startMin < block.endMinutes + (block.kind === 'unavailable' || block.kind === 'time_off' ? 0 : buffer) &&
        endMin > block.startMinutes - (block.kind === 'unavailable' || block.kind === 'time_off' ? 0 : buffer),
    );
  }, [selected, blocksByDay, durationMinutes, buffer]);

  const pick = (day: string, offsetRatio: number) => {
    const raw = startHour * 60 + offsetRatio * windowMinutes;
    const snapped = Math.max(0, Math.min(24 * 60 - durationMinutes, Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES));
    onPick(day, `${String(Math.floor(snapped / 60)).padStart(2, '0')}:${String(snapped % 60).padStart(2, '0')}`);
  };

  const scopeLabel =
    userIds.length === 0
      ? t('availability.scopeCompany')
      : t('availability.scopePeople', { names: (view?.people ?? []).map((person) => person.fullName).join(', ') });

  return (
    <div className="mb-4 rounded-lg border border-[color:var(--color-line)] p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold">{t('availability.heading')}</p>
          <p className="text-sm text-[color:var(--color-muted)]">{scopeLabel}</p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            aria-label={t('availability.previousWeek')}
            className="min-w-11 text-xl"
            onClick={() => setAnchor(addDaysToDate(start, -7))}
          >
            ‹
          </Button>
          <Button variant="ghost" onClick={() => setAnchor(todayInTimezone(view?.timezone))}>
            {t('availability.today')}
          </Button>
          <Button
            variant="ghost"
            aria-label={t('availability.nextWeek')}
            className="min-w-11 text-xl"
            onClick={() => setAnchor(addDaysToDate(start, 7))}
          >
            ›
          </Button>
        </div>
      </div>

      {view && view.staleUserIds.length > 0 ? (
        <div className="mb-2">
          <Alert tone="warning">
            {t('availability.staleWarning', { names: view.staleUserIds.map(nameOf).join(', ') })}
          </Alert>
        </div>
      ) : null}
      {view?.truncated ? (
        <div className="mb-2">
          <Alert tone="warning">{t('availability.truncatedWarning')}</Alert>
        </div>
      ) : null}

      {availability.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-[color:var(--color-muted)]">
          <Spinner /> {t('availability.loading')}
        </div>
      ) : availability.isError ? (
        <Alert tone="error">{t('availability.loadError')}</Alert>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[640px]">
            {/* Day headers */}
            <div className="grid" style={{ gridTemplateColumns: '3.5rem repeat(7, minmax(0, 1fr))' }}>
              <div />
              {days.map((day) => (
                <p
                  key={day}
                  className={
                    'border-b border-[color:var(--color-line)] px-1 pb-1 text-center text-xs font-semibold ' +
                    (day === todayInTimezone(view?.timezone) ? 'text-[color:var(--color-brand-strong)]' : '')
                  }
                >
                  {formatShortDate(day)}
                </p>
              ))}
            </div>

            {/* Time gutter + seven clickable columns */}
            <div className="grid" style={{ gridTemplateColumns: '3.5rem repeat(7, minmax(0, 1fr))' }}>
              <div className="relative" style={{ height: `${columnHeight}px` }}>
                {Array.from({ length: endHour - startHour }, (_, index) => (
                  <p
                    key={index}
                    className="absolute right-1 -translate-y-1/2 text-[10px] text-[color:var(--color-muted)]"
                    style={{ top: `${(index / (endHour - startHour)) * 100}%` }}
                  >
                    {formatLocalTime(`${String(startHour + index).padStart(2, '0')}:00`)}
                  </p>
                ))}
              </div>

              {days.map((day) => {
                const blocks = blocksByDay.get(day) ?? [];
                return (
                  <div
                    key={day}
                    role="button"
                    tabIndex={0}
                    aria-label={t('availability.pickOn', { date: formatShortDate(day) })}
                    className="relative cursor-pointer border-l border-[color:var(--color-line)] bg-[color:var(--color-surface)] hover:bg-[color:var(--color-brand-soft)]/30 focus-visible:outline-2"
                    style={{
                      height: `${columnHeight}px`,
                      backgroundImage:
                        'repeating-linear-gradient(to bottom, transparent, transparent ' +
                        `${HOUR_HEIGHT - 1}px, var(--color-line) ${HOUR_HEIGHT - 1}px, var(--color-line) ${HOUR_HEIGHT}px)`,
                    }}
                    onClick={(event) => {
                      const bounds = event.currentTarget.getBoundingClientRect();
                      // A zero-height rect (jsdom, a hidden ancestor) reads as
                      // the top of the window rather than NaN o'clock.
                      pick(day, bounds.height > 0 ? (event.clientY - bounds.top) / bounds.height : 0);
                    }}
                    onKeyDown={(event) => {
                      // Keyboard path: pick the first open morning slot; fine
                      // tuning happens in the time field beside the grid.
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        pick(day, ((9 - startHour) * 60) / windowMinutes);
                      }
                    }}
                  >
                    {offHoursBands(day).map((band, index) => (
                      <div
                        key={`off-${index}`}
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 bg-[color:var(--color-line)]/25"
                        style={{ top: `${band.top}%`, height: `${band.height}%` }}
                      />
                    ))}
                    {blocks.map((block) => (
                      <BusyBlock key={block.key} block={block} buffer={buffer} yOf={yOf} heightOf={heightOf} />
                    ))}

                    {selected && selected.date === day ? (
                      <div
                        aria-hidden
                        className={
                          'pointer-events-none absolute inset-x-0.5 z-20 rounded border-2 border-dashed ' +
                          (selectionClash
                            ? 'border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)]/60'
                            : 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]/60')
                        }
                        style={{
                          top: `${yOf(minutesOf(selected.time))}%`,
                          height: `${heightOf(durationMinutes)}%`,
                        }}
                      >
                        <p className="truncate px-1 text-[10px] font-semibold">
                          {formatLocalTime(selected.time)} · {t('availability.thisVisit')}
                        </p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[color:var(--color-muted)]">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm bg-[color:var(--color-brand-soft)] ring-1 ring-[color:var(--color-brand)]" />
          {t('availability.legendVisit')}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm bg-[color:var(--color-warning-soft)] ring-1 ring-[color:var(--color-warning)]" />
          {t('availability.legendUnassigned')}
        </span>
        <span className="inline-flex items-center gap-1">
          <span
            className="inline-block h-3 w-3 rounded-sm ring-1 ring-[color:var(--color-line)]"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg, var(--color-line), var(--color-line) 2px, transparent 2px, transparent 4px)',
            }}
          />
          {t('availability.legendExternal')}
        </span>
        <span>
          {t('planning.unavailable')} · {t('planning.timeOff')}
        </span>
        {buffer > 0 ? <span>{t('availability.legendBuffer', { minutes: buffer })}</span> : null}
        <span>{t('availability.clickHint')}</span>
      </div>

      {selected && selectionClash ? (
        <p className="mt-2 text-sm text-[color:var(--color-danger)]">{t('availability.selectionClash')}</p>
      ) : null}
    </div>
  );
}

function BusyBlock({
  block,
  buffer,
  yOf,
  heightOf,
}: {
  block: DayBlock;
  buffer: number;
  yOf: (minutes: number) => number;
  heightOf: (minutes: number) => number;
}) {
  const { t } = useTranslation();
  const tones: Record<DayBlock['kind'], string> = {
    visit: 'bg-[color:var(--color-brand-soft)] ring-1 ring-[color:var(--color-brand)]',
    unassigned: 'bg-[color:var(--color-warning-soft)] ring-1 ring-[color:var(--color-warning)]',
    unavailable: 'bg-[color:var(--color-line)] opacity-70',
    time_off: 'ring-1 ring-[color:var(--color-line)] bg-[color:var(--color-surface)]',
    external: 'ring-1 ring-[color:var(--color-line)] bg-[color:var(--color-surface)]',
  };
  // The block itself is a few millimeters wide and truncates almost anything,
  // so what "unassigned" actually means lives in the tooltip, where there is
  // room to say it: nobody picked yet, and that is why it holds the hour.
  const title =
    block.personNames.length > 0
      ? `${block.label} — ${block.personNames.join(', ')}`
      : block.kind === 'unassigned'
        ? t('availability.unassignedTitle')
        : block.label;

  return (
    <>
      {buffer > 0 ? (
        // The travel padding around the work, drawn faintly: time the person
        // is occupied getting there and packing up, not a free-looking gap.
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0.5 rounded bg-[color:var(--color-line)]/40"
          style={{
            top: `${yOf(Math.max(0, block.startMinutes - buffer))}%`,
            height: `${heightOf(block.endMinutes - block.startMinutes + 2 * buffer)}%`,
          }}
        />
      ) : null}
      <div
        title={title}
        aria-label={title}
        className={`absolute inset-x-0.5 z-10 cursor-not-allowed overflow-hidden rounded px-1 ${tones[block.kind]}`}
        style={{
          top: `${yOf(block.startMinutes)}%`,
          height: `${Math.max(1.5, heightOf(block.endMinutes - block.startMinutes))}%`,
          ...(block.kind === 'external' || block.kind === 'time_off'
            ? {
                backgroundImage:
                  'repeating-linear-gradient(45deg, var(--color-line), var(--color-line) 2px, transparent 2px, transparent 6px)',
              }
            : {}),
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <p className="truncate text-[10px] font-semibold leading-tight">{block.label}</p>
        {block.personNames.length > 0 ? (
          <p className="truncate text-[10px] leading-tight text-[color:var(--color-muted)]">
            {block.personNames.join(', ')}
          </p>
        ) : block.kind === 'unassigned' ? (
          <p className="truncate text-[10px] leading-tight text-[color:var(--color-muted)]">
            {t('availability.nobodyAssignedYet')}
          </p>
        ) : null}
      </div>
    </>
  );
}

function minutesOf(timeHHMM: string): number {
  const [hour = '0', minute = '0'] = timeHHMM.split(':');
  return Number(hour) * 60 + Number(minute);
}
